import { Controller } from "@hotwired/stimulus"
import { destroy, patch, post } from "@rails/request.js"

/**
 * The two ids that are tree items and not rows. They have no `drawing_subsets`
 * record, so they can never appear in a PATCH — their state travels in the
 * drawing's `layer_state` instead.
 */
const VIRTUAL_CONTAINERS = new Set(["everything", "shapes"])

/**
 * Subsets (Groups) — the Drawing v2 layer tree and its CRUD.
 *
 * A port of v1's `use-subsets-crud.ts` + `subsets-tab.tsx`, with two shapes
 * changed because Hotwire has better answers than React did:
 *
 *  - **The tree re-renders on the server.** v1 patches its React tree in place
 *    after every change. Building rows here would put every label, every
 *    tooltip and ten locales' worth of copy into JavaScript, so the tree lives
 *    in a Turbo Frame and this reloads it. Visibility and lock do NOT reload —
 *    they change one icon, and a whole-tree round trip for that would be slower
 *    and would throw away the scroll position.
 *
 *  - **The canvas is reached through DOM events, not a shared object.** The
 *    engine is mounted by `facilities--floorplan-canvas`; this controller
 *    dispatches and that one forwards. Two Stimulus controllers reaching into
 *    one mounted canvas would make the sidebar impossible to render without it.
 *
 * ## Optimism is inconsistent in v1, on purpose, and that is copied
 *
 * Create AWAITS the server, because the row needs the id it generates. Update,
 * delete and the visibility/lock toggles are optimistic. Only delete rolls
 * back, and only on a rejected promise — `@rails/request.js` resolves with
 * `response.ok === false` for an HTTP error, so a 422 leaves the row gone from
 * the UI in v1 too.
 *
 * ## `save:content-version` is load-bearing, twice
 *
 * The subsets API bumps the drawing's `content_version` out of band. Without
 * telling `SaveManager`, the next autosave sends a stale base version and 409s
 * on EVERY group operation. It is also what triggers per-subset preview
 * regeneration. Emitted after create, update, delete and duplicate — and
 * deliberately NOT after a visibility or lock toggle, because `batch_update`
 * does not bump it (bumping there would race the canvas delta save into a
 * version conflict).
 */
export default class extends Controller {
  static targets = [
    "entityTemplates",
    "header",
    "selectedCount",
    "deleteSelected",
    "form",
    "formName",
    "formDescription",
    "formError",
    "formHasDrawingArea",
    "formColorRow",
    "formColor",
  ]
  static values = { drawingId: String, reloadPath: String }

  connect() {
    // Tell the canvas what the tree holds, now and after every reload. Without
    // it an entity whose group was just deleted keeps a `subsetId` the canvas
    // still believes in, and its row never falls back to a container.
    this.boundTreeLoaded = () => {
      this.#publishKnownSubsets()
      this.#paintEntityRows()
    }
    this.element.addEventListener("turbo:frame-load", this.boundTreeLoaded)

    /** Row ids the user has ticked. Only folders they may delete count. */
    this.selected = new Set()
    /** Set while editing an existing row; null while creating a new one. */
    this.editingId = null
    /** The row a confirmation dialog is about to act on. */
    this.pendingId = null
    /** The shape the group being edited is already drawn as, if any. */
    this.editingBoundsEntityId = null
    /** The drawing item being dragged, as opposed to a group. */
    this.draggingEntityId = null
    /**
     * Held across the draw-area confirmation. v1 keeps the same split: the
     * checkbox asks first and only then changes anything, because ticking it
     * moves whatever the group already holds out to "Everything else".
     */
    this.pendingDrawingArea = false

    this.#publishKnownSubsets()
  }

  disconnect() {
    if (!this.boundTreeLoaded) return

    this.element.removeEventListener("turbo:frame-load", this.boundTreeLoaded)
    this.boundTreeLoaded = null
  }

  // ── The inline form ───────────────────────────────────────────────────────

  openCreateForm() {
    this.editingId = null
    this.editingBoundsEntityId = null
    this.#fillForm({
      name: "",
      description: "",
      color: "",
      hasDrawingArea: false,
    })
    this.formTarget.hidden = false
    this.#nameInput()?.focus()
  }

  editSubset(event) {
    const row = event.target.closest("[data-subset-id]")
    if (!row) return

    this.editingId = row.dataset.subsetId
    // Kept because the save needs to tell the canvas whether this group already
    // has a shape — and the row is gone from the DOM by the time it answers.
    this.editingBoundsEntityId = row.dataset.subsetBoundsEntityId ?? null
    this.#fillForm({
      name: row.dataset.subsetName ?? "",
      description: row.dataset.subsetDescription ?? "",
      color: row.dataset.subsetColor ?? "",
      hasDrawingArea: row.dataset.subsetHasDrawingArea === "true",
    })
    this.formTarget.hidden = false
    this.#nameInput()?.focus()
  }

  closeForm() {
    this.formTarget.hidden = true
    this.editingId = null
    this.editingBoundsEntityId = null
    this.clearFormError()
  }

  clearFormError() {
    this.formErrorTarget.hidden = true
    this.formErrorTarget.textContent = ""
  }

  /**
   * Ticking the box is a question before it is a change.
   *
   * v1 asks first because enabling a drawing area moves every item already in
   * the group out to "Everything else" — they can only come back by being
   * dragged inside the new area. Unticking asks nothing.
   */
  toggleDrawingArea(event) {
    const checked = event.target.checked
    if (!checked) {
      this.formColorRowTarget.hidden = true
      return
    }

    // Only an existing group can already hold anything, so a brand-new one
    // skips the question entirely.
    if (!this.editingId) {
      this.formColorRowTarget.hidden = false
      return
    }

    this.pendingDrawingArea = true
    event.target.checked = false
    this.#openDialog("subset-draw-area-modal")
  }

  drawAreaConfirmed() {
    this.#closeDialog("subset-draw-area-modal")
    if (!this.pendingDrawingArea) return

    this.pendingDrawingArea = false
    this.formHasDrawingAreaTarget.checked = true
    this.formColorRowTarget.hidden = false
  }

  async saveForm() {
    const name = this.#nameInput()?.value.trim() ?? ""

    // v1 disables Save while this is empty. The project rule is that a submit
    // stays clickable and the reason goes inline, so the same state is reported
    // rather than being silently unreachable.
    if (!name) {
      this.formErrorTarget.textContent =
        this.formErrorTarget.dataset.requiredMessage ?? ""
      this.formErrorTarget.hidden = false
      return
    }

    const hasDrawingArea = this.#drawingAreaChecked()
    // Unticking the box on a group that HAS an outline is an unlink, and it has
    // to be said explicitly: `Drawings::UpdateSubset` only clears
    // `has_drawing_area` when the client sends `bounds_entity_id: null`, and
    // omitting the key leaves the row pointing at a rectangle nothing owns —
    // with the rectangle still on the canvas. CONEK-2591.
    const unlinking = !hasDrawingArea && !!this.editingBoundsEntityId

    const payload = {
      name,
      description: this.#descriptionInput()?.value ?? "",
      has_drawing_area: hasDrawingArea,
      // A group with no drawing area has no colour at all — v1 sends undefined
      // for both branches rather than keeping a colour nothing will paint.
      color: hasDrawingArea ? this.#colorValue() : null,
      ...(unlinking ? { bounds_entity_id: null } : {}),
    }

    const editing = this.editingId
    const boundsEntityId = editing ? this.editingBoundsEntityId : null

    const response = editing
      ? await patch(this.#subsetPath(editing), { body: { subset: payload } })
      : await post(this.#subsetsPath(), { body: { subset: payload } })

    if (!response.ok) return

    const body = await this.#safeJson(response)
    this.closeForm()
    this.#adoptContentVersion(body)
    this.#reloadTree()

    if (unlinking) {
      // The shape goes with the link. Leaving it behind is a rectangle nobody
      // can account for and nothing can select as a boundary again.
      this.#tellCanvas("subsets:remove-boundary", { subsetId: editing })
    }

    if (!hasDrawingArea) return

    // Ask the canvas to draw the area. It answers on `subsets:boundary-created`
    // with the shape it made, and only then is the group actually linked to it
    // — creating a drawing area is two round trips, never one.
    //
    // The canvas decides whether to draw at all: a row can point at a shape the
    // user has since deleted, and that has to draw a NEW one rather than
    // silently do nothing. Only the canvas can see the Scene, so only the
    // canvas can tell.
    this.#tellCanvas("subsets:ensure-boundary", {
      subsetId: editing ?? body?.subset?.id,
      name: payload.name,
      color: payload.color,
      boundsEntityId,
    })
  }

  /**
   * The canvas drew a boundary: link the row to it.
   *
   * Two things have to happen and neither is optional. The row needs its
   * `bounds_entity_id` or the outline is gone on the next reload; and the
   * canvas's own boundary index needs the pair too, because it was filled once
   * from the page and nothing re-feeds it — without this the group is drawn but
   * nothing dropped inside it is ever assigned to it.
   */
  async boundaryCreated(event) {
    const { subsetId, entityId, name } = event.detail
    if (!subsetId || !entityId) return

    this.#tellCanvas("subsets:boundary-added", {
      subsetId,
      entityId,
      name,
      color: null,
    })

    const response = await patch(this.#subsetPath(subsetId), {
      body: { subset: { bounds_entity_id: entityId } },
    })
    if (!response.ok) return

    this.#adoptContentVersion(await this.#safeJson(response))
    this.#reloadTree()
  }

  // ── The drawing's own items, as rows under each group ─────────────────────

  /**
   * Paint the leaf rows the canvas just told us about.
   *
   * The rows are not stored anywhere — `entity.subsetId` is the only persisted
   * fact, and Shapes-vs-Everything is derived from the entity's type every
   * time. So this is a full repaint on every change rather than a patch: the
   * canvas already did the deciding, and an incremental version here would be
   * a second place for the rules to drift.
   *
   * The MARKUP comes from a server-rendered `<template>` per type, so the
   * eighteen type names and both tooltips stay translatable.
   */
  renderEntityRows(event) {
    // Cached because the tree is re-rendered from the server after every
    // create, rename and delete, and that replaces the whole list — entity
    // rows included. The canvas has no reason to re-send them (no entity
    // changed), so without this the drawing's items vanish from the tree the
    // first time a group is added.
    this.entityRows = event.detail?.rows ?? this.entityRows ?? []
    this.#paintEntityRows()
  }

  #paintEntityRows() {
    const rows = this.entityRows ?? []
    const lists = new Map()

    // Clear what is there, remembering which list belongs to which parent.
    for (const li of this.element.querySelectorAll("[data-entity-row]"))
      li.remove()

    for (const row of rows) {
      const list = lists.get(row.parentId) ?? this.#childListFor(row.parentId)
      if (!list) continue
      lists.set(row.parentId, list)

      const node = this.#buildEntityRow(row)
      if (node) list.appendChild(node)
    }
  }

  toggleEntityVisibility(event) {
    this.#toggleEntityFlag(event, "entityVisible", "visible")
  }

  toggleEntityLock(event) {
    this.#toggleEntityFlag(event, "entityLocked", "locked")
  }

  // ── Visibility and lock ───────────────────────────────────────────────────

  /**
   * Optimistic, and NOT a tree reload: one icon changes.
   *
   * The two virtual containers never reach the API — they have no row. Their
   * state lives in the drawing's `layer_state`, written by the canvas save, so
   * the canvas is told instead.
   */
  toggleVisibility(event) {
    this.#toggleFlag(event, {
      attribute: "subsetVisible",
      field: "hidden",
      // The eye — the "on" icon — is what a VISIBLE row shows.
      onWhen: (visible) => visible,
      // `hidden` is stored and `visible` is rendered; they are inverses, and
      // getting this backwards hides every layer it touches.
      persisted: (visible) => !visible,
    })
  }

  toggleLock(event) {
    this.#toggleFlag(event, {
      attribute: "subsetLocked",
      field: "locked",
      // The OPEN padlock is the "on" icon, so "on" here means NOT locked. The
      // two toggles disagree about which way round their attribute runs, which
      // is exactly why this is spelled out per toggle instead of assumed.
      onWhen: (locked) => !locked,
      persisted: (locked) => locked,
    })
  }

  // ── Delete ────────────────────────────────────────────────────────────────

  confirmDelete(event) {
    const row = event.target.closest("[data-subset-id]")
    if (!row) return

    this.pendingId = row.dataset.subsetId
    this.#openDialog("subset-delete-modal")
  }

  async deleteConfirmed() {
    this.#closeDialog("subset-delete-modal")
    const id = this.pendingId
    if (!id) return
    this.pendingId = null

    // The group's outline exists only to delimit it, so it goes with it.
    // Before the DELETE, because the row is what tells us there was one.
    this.#tellCanvas("subsets:remove-boundary", { subsetId: id })

    const response = await destroy(this.#subsetPath(id))
    if (!response.ok) return

    this.selected.delete(id)
    this.#renderSelection()
    this.#adoptContentVersion(await this.#safeJson(response))
    this.#reloadTree()
  }

  confirmDeleteSelected() {
    if (this.selected.size < 2) return
    this.#openDialog("subsets-delete-modal")
  }

  async deleteSelectedConfirmed() {
    this.#closeDialog("subsets-delete-modal")
    const ids = [...this.selected]
    if (!ids.length) return

    // Sequential rather than parallel: each delete bumps the drawing's
    // content_version, and the last response is the one worth adopting.
    let last = null
    for (const id of ids) {
      this.#tellCanvas("subsets:remove-boundary", { subsetId: id })
      const response = await destroy(this.#subsetPath(id))
      if (response.ok) last = await this.#safeJson(response)
    }

    this.selected.clear()
    this.#renderSelection()
    this.#adoptContentVersion(last)
    this.#reloadTree()
  }

  // ── Duplicate ─────────────────────────────────────────────────────────────

  /**
   * Copies the group itself, not what it holds.
   *
   * v1's duplicate also clones the group's canvas entities. That half belongs
   * with the entity rows — it needs the tree to know its members — so it lands
   * with them rather than being half-done here.
   */
  async duplicateSubset(event) {
    const row = event.target.closest("[data-subset-id]")
    if (!row) return

    // The duplicate lands beside the original, not at the root. A nested group
    // duplicated to the top of the tree is a silent move, not a copy.
    const parent = row.parentElement?.closest("[data-subset-id]")
    const parentId =
      parent && parent.dataset.subsetKind === "subset"
        ? parent.dataset.subsetId
        : null

    const response = await post(this.#subsetsPath(), {
      body: {
        subset: {
          name: row.dataset.subsetName,
          description: row.dataset.subsetDescription ?? "",
          color: row.dataset.subsetColor ?? null,
          has_drawing_area: false,
          parent_id: parentId,
        },
      },
    })
    if (!response.ok) return

    this.#adoptContentVersion(await this.#safeJson(response))
    this.#reloadTree()
  }

  // ── Drag and drop ─────────────────────────────────────────────────────────

  dragStart(event) {
    // `currentTarget`, not a `closest` from the target — and stop the event
    // here. Every row is draggable and every row carries this action, so a
    // dragstart on a CHILD also bubbles to its parent, whose handler runs last
    // and overwrites the id: dragging a nested group moved its parent instead.
    const row = event.currentTarget.closest("[data-subset-id]")
    if (!row) return

    event.stopPropagation()
    if (this.#onlyLooking(event)) return
    this.draggingId = row.dataset.subsetId
    // Cleared, not just left alone: `#applyDrop` checks the entity first, so a
    // stale id here would quietly turn a group drag into an item move.
    this.draggingEntityId = null
    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/plain", row.dataset.subsetId)
  }

  /**
   * Drag one of the drawing's items, not the row it sits in.
   *
   * The entity rows are cloned from a template and are draggable in their own
   * right — without that the browser walks up to the nearest draggable
   * ancestor, which is the container's row, and moving a single shape dragged
   * the whole container instead.
   */
  dragStartEntity(event) {
    const row = event.currentTarget.closest("[data-entity-id]")
    if (!row) return

    event.stopPropagation()
    if (this.#onlyLooking(event)) return
    this.draggingEntityId = row.dataset.entityId
    this.draggingId = null
    event.dataTransfer.effectAllowed = "move"
    // Some browsers refuse to begin a drag that carries nothing at all.
    event.dataTransfer.setData("text/plain", row.dataset.entityId)
  }

  /**
   * Whether the plan is only being looked at (`data-floorplan-mode` on the
   * editor's root): the rows stay draggable markup, and moving one is a change
   * to the plan — so the drag is not started. Somebody who may only look gets
   * rows that are not draggable at all.
   */
  #onlyLooking(event) {
    if (!this.element.closest("[data-floorplan-mode='view']")) return false

    event.preventDefault()
    return true
  }

  /**
   * BOTH ids, not just the entity one.
   *
   * `dragend` is the only signal a drag ended WITHOUT a drop — Escape, or a
   * release outside every zone — and `#applyDrop` is what clears the ids
   * otherwise. Leaving `draggingId` set meant the next drop the panel saw,
   * from any source at all (a file off the desktop, an image from another
   * tab), passed `dropOnRoot`'s guard and re-parented the abandoned group.
   */
  dragEnd() {
    this.draggingEntityId = null
    this.draggingId = null
    this.#clearDropHighlight()
  }

  dragOver(event) {
    if (!this.draggingId && !this.draggingEntityId) return
    event.preventDefault()
    event.currentTarget.classList.add("bg-primary/10")
  }

  dragLeave(event) {
    event.currentTarget.classList.remove("bg-primary/10")
  }

  async drop(event) {
    event.preventDefault()
    // The row owns this one. `drop` bubbles, and the scroller below also
    // listens — without stopping here the same drop is applied twice.
    event.stopPropagation()
    this.#clearDropHighlight()

    await this.#applyDrop(event.target.closest("[data-subset-id]"))
  }

  dragOverRoot(event) {
    if (this.draggingId || this.draggingEntityId) event.preventDefault()
  }

  async dropOnRoot(event) {
    if (!this.draggingId && !this.draggingEntityId) return

    event.preventDefault()
    this.#clearDropHighlight()

    // The scroller catches every drop the row itself did not, and there are
    // more of those than there look: only a row's TITLE line carries the drop
    // handler, so dropping into an expanded group's body — or onto an item
    // already inside it — lands here. Resolving the nearest row from the drop
    // point makes the whole group a target, which is what it looks like.
    await this.#applyDrop(event.target.closest("[data-subset-id]"))
  }

  /**
   * Apply a drop onto `target`, or onto the drawing itself when there is none.
   *
   * Two different writes hide behind one gesture. Moving one of the drawing's
   * ITEMS sets `entity.subsetId` on the Scene; moving a GROUP re-parents a row
   * through the API. A drop on a virtual container means "no group" for both.
   */
  async #applyDrop(target) {
    // Both ids are taken and cleared before ANY await. `drop` bubbles, so a
    // second listener runs while the first is still suspended on its PATCH —
    // and a flag cleared after the await is still set when it looks. That sent
    // two `parent_id` PATCHes for one drag: doubled traffic, a double tree
    // reload, and two writers racing the `content_version` this whole surface
    // depends on. Clearing here makes the second pass a no-op whatever the
    // event plumbing does.
    const entityId = this.draggingEntityId
    const subsetId = this.draggingId
    this.draggingEntityId = null
    this.draggingId = null

    if (entityId) {
      this.#tellCanvas("subsets:assign-entity", {
        entityId,
        subsetId: this.#groupIdOf(target),
      })
      return
    }

    if (!subsetId || target?.dataset.subsetId === subsetId) return

    // A group cannot be dropped inside itself — that would orphan the branch.
    //
    // The source row can be gone by now: a Turbo Frame reload from anything
    // else — a flag toggle's round trip, another change arriving — replaces the
    // whole tree mid-drag. Reading `dataset` off that null threw an uncaught
    // rejection instead of doing nothing.
    const sourceRow = document.querySelector(`[data-subset-id='${subsetId}']`)
    if (!sourceRow) return

    if (
      target &&
      this.#branchIdsFrom(sourceRow).includes(target.dataset.subsetId)
    ) {
      return
    }

    await this.#reparent(subsetId, this.#groupIdOf(target))
  }

  /**
   * The group a drop landed in, or null.
   *
   * A virtual container is not a row, so it can never be a `parent_id` and can
   * never be an `entity.subsetId` — v1 sends null for all three virtual parents
   * and recovers the placement from the saved order map instead.
   */
  #groupIdOf(target) {
    if (!target || target.dataset.subsetKind === "container") return null

    return target.dataset.subsetId
  }

  // ── Selection ─────────────────────────────────────────────────────────────

  toggleSelection(event) {
    const row = event.target.closest("[data-subset-id]")
    if (!row || row.dataset.subsetDeletable !== "true") return

    const id = row.dataset.subsetId
    if (this.selected.has(id)) this.selected.delete(id)
    else this.selected.add(id)

    row.dataset.subsetSelected = String(this.selected.has(id))
    this.#renderSelection()
  }

  // ── Private ───────────────────────────────────────────────────────────────

  async #reparent(id, parentId) {
    const response = await patch(this.#subsetPath(id), {
      body: { subset: { parent_id: parentId } },
    })
    if (!response.ok) return

    this.#adoptContentVersion(await this.#safeJson(response))
    this.#reloadTree()

    // The sibling order has no endpoint — it travels to the drawing's
    // `layer_state` through the canvas save, which is why the canvas has to be
    // told even though the parent change went to the API.
    this.#tellCanvas("subsets:order-changed", { order: this.#orderMap() })
  }

  /**
   * Flip one row flag, its icon and its label.
   *
   * `next` is the row attribute AFTER the click, in the attribute's OWN sense —
   * `subsetVisible` counts up to visible, `subsetLocked` counts up to locked.
   * The two run opposite ways, so `onWhen` converts each into the one question
   * the rest of this method asks: is the button in its "on" state?
   *
   * Once that is settled everything else follows from it uniformly — the "on"
   * icon shows, the "off" icon hides, the label names the action available NEXT,
   * and `aria-pressed` is the negation because "pressed" means hidden or locked.
   * Both icons are already in the button; only which one is hidden changes.
   */
  #toggleFlag(event, { attribute, field, onWhen, persisted }) {
    const button = event.currentTarget
    const row = button.closest("[data-subset-id]")
    if (!row) return

    const next = row.dataset[attribute] !== "true"
    row.dataset[attribute] = String(next)

    // The wrapping <span>'s `hidden` ATTRIBUTE. Not the <svg>'s property (it
    // has none) and not a `hidden` class (it loses to the `inline-block` the
    // icon helper emits, because two display utilities are decided by
    // stylesheet order). Both of those shipped, and both showed the two icons
    // of every toggle at once.
    const on = onWhen(next)
    button.querySelector("[data-state-icon='on']").hidden = !on
    button.querySelector("[data-state-icon='off']").hidden = on
    button.setAttribute(
      "aria-label",
      on ? button.dataset.onLabel : button.dataset.offLabel,
    )
    button.setAttribute("aria-pressed", String(!on))

    // Not a display filter: hiding a group hides what is IN it, and locking it
    // locks them. v1 writes the value onto every descendant entity and every
    // nested folder, so a row that only dimmed itself would be a lie.
    this.#tellCanvas("subsets:cascade-flag", {
      parentIds: this.#branchIdsFrom(row),
      flag: field === "hidden" ? "visible" : "locked",
      value: field === "hidden" ? next : persisted(next),
    })

    if (row.dataset.subsetKind === "container") {
      // No row to PATCH. The canvas owns this pair for the two containers, and
      // persists it in the drawing's own `layer_state`.
      this.#tellCanvas("subsets:container-changed", {
        container: row.dataset.subsetId,
        visible: row.dataset.subsetVisible === "true",
        locked: row.dataset.subsetLocked === "true",
      })
      return
    }

    // Every nested group too, not just the one clicked. The cascade already
    // moved their contents; without this their OWN eye and padlock revert to
    // the stale database value on the next reload, while the contents stay
    // hidden — the tree then disagrees with itself. v1 batches the whole
    // branch for the same reason.
    //
    // Fire and forget, as v1 does — and deliberately no content-version adopt:
    // `batch_update` does not bump it, precisely so it cannot race the canvas
    // delta save into a version conflict.
    const branch = this.#branchIdsFrom(row).filter(
      (id) => !VIRTUAL_CONTAINERS.has(id),
    )
    if (!branch.length) return

    patch(this.#batchPath(), {
      body: {
        subsets: branch.map((id) => ({ id, [field]: persisted(next) })),
      },
    })
  }

  /**
   * The `<ul>` a container's or group's children hang in.
   *
   * Every row renders as a `<details>` even when empty (`always_parent`),
   * precisely so this list exists before there is anything to put in it.
   */
  #childListFor(parentId) {
    const row =
      this.element.querySelector(`[data-subset-id='${parentId}']`) ??
      this.#soleContainer()
    return row?.querySelector("details > ul") ?? null
  }

  /**
   * The one node a FLAT tree renders, or null when the tree is the ordinary one.
   *
   * `parentFor` assigns every row a parent before it knows what the tree looks
   * like: a rectangle, a text or the background-image pin all come back parented
   * to `shapes`. The IoT editor's tree has no `shapes` node — it has one node,
   * "Entities" (`SubsetsTreeComponent#nodes`, flat) — so those rows resolved to
   * no list and `#paintEntityRows` dropped them on the floor. The sidebar simply
   * never listed a shape.
   *
   * Keyed on the tree having exactly ONE `[data-subset-id]`, which is true only
   * of the flat tree: the ordinary one always renders both virtual containers,
   * so this returns null there and the lookup above is unchanged for v1.
   */
  #soleContainer() {
    const rows = this.element.querySelectorAll("[data-subset-id]")
    return rows.length === 1 ? rows[0] : null
  }

  #buildEntityRow(row) {
    const template =
      this.entityTemplatesTarget.querySelector(
        `template[data-entity-type='${row.type}']`,
      ) ??
      this.entityTemplatesTarget.querySelector(
        "template[data-entity-type='unknown']",
      )
    if (!template) return null

    const node = template.content.firstElementChild.cloneNode(true)
    node.dataset.entityId = row.entityId
    // Which KIND of entity this row is. The template it was cloned from knows,
    // and the clone did not — so from outside, a barrier row and a device row
    // were distinguishable only by their translated label.
    node.dataset.entityType = row.type
    node.dataset.entityVisible = String(row.visible)
    node.dataset.entityLocked = String(row.locked)

    // A catalogue name wins over the type's label — v1 does the same, and it is
    // why a product row reads "A-SAFE iFlex" rather than "Product".
    if (row.displayName) {
      node.querySelector("[data-entity-name]").textContent = row.displayName
    }

    this.#paintEntityToggle(node, "visibility", row.visible)
    this.#paintEntityToggle(node, "lock", !row.locked)
    return node
  }

  /** `on` is the state the first icon stands for: visible, or unlocked. */
  #paintEntityToggle(node, action, on) {
    const button = node.querySelector(`[data-entity-action='${action}']`)
    if (!button) return

    button.querySelector("[data-state-icon='on']").hidden = !on
    button.querySelector("[data-state-icon='off']").hidden = on
    button.setAttribute(
      "aria-label",
      on ? button.dataset.onLabel : button.dataset.offLabel,
    )
    button.setAttribute("aria-pressed", String(!on))
  }

  /**
   * Flip one entity's own flag, on the row and on the canvas.
   *
   * `next` is the attribute after the click, in its own sense: `entityVisible`
   * counts up to VISIBLE, `entityLocked` counts up to LOCKED. The first icon
   * always stands for the unset state — the eye, the open padlock — so the two
   * disagree about which way round that is, exactly as the folder toggles do.
   */
  #toggleEntityFlag(event, attribute, flag) {
    const button = event.currentTarget
    const row = button.closest("[data-entity-id]")
    if (!row) return

    const next = row.dataset[attribute] !== "true"
    row.dataset[attribute] = String(next)
    this.#paintEntityToggle(
      row,
      flag === "visible" ? "visibility" : "lock",
      flag === "visible" ? next : !next,
    )

    this.#tellCanvas("subsets:entity-flag", {
      entityId: row.dataset.entityId,
      flag,
      value: next,
    })
  }

  #fillForm({ name, description, color, hasDrawingArea }) {
    this.clearFormError()
    const nameInput = this.#nameInput()
    if (nameInput) nameInput.value = name
    const descriptionInput = this.#descriptionInput()
    if (descriptionInput) descriptionInput.value = description

    this.formHasDrawingAreaTarget.checked = hasDrawingArea
    this.formColorRowTarget.hidden = !hasDrawingArea

    const colorInput = this.#colorInput()
    if (colorInput && color) colorInput.value = color
  }

  // `Ui::InputComponent` and `Ui::CheckboxComponent` put the caller's `data:`
  // straight onto the <input>, so these targets ARE the fields. Only
  // `Ui::ColorFieldComponent` differs — it rides the data on its wrapper,
  // because the wrapper is what the swatch, the hex box and the presets share.
  #nameInput() {
    return this.formNameTarget
  }

  #descriptionInput() {
    return this.formDescriptionTarget
  }

  #colorInput() {
    return this.formColorTarget.querySelector("input[type='color']")
  }

  #colorValue() {
    return this.#colorInput()?.value || null
  }

  #drawingAreaChecked() {
    return !!this.formHasDrawingAreaTarget.checked
  }

  /** `Record<parentId | "root", childId[]>`, the shape the canvas persists. */
  #orderMap() {
    const order = {}
    for (const row of this.element.querySelectorAll("[data-subset-id]")) {
      const parent = row.parentElement?.closest("[data-subset-id]")
      const key = parent ? parent.dataset.subsetId : "root"
      ;(order[key] ??= []).push(row.dataset.subsetId)
    }
    return order
  }

  #renderSelection() {
    const count = this.selected.size
    // Strictly above one, as v1: a single selection shows no number and offers
    // no bulk delete.
    this.selectedCountTarget.hidden = count < 2
    this.selectedCountTarget.textContent = count < 2 ? "" : `(${count})`
    this.deleteSelectedTarget.hidden = count < 2
    // Under its tab the header has no title, so it shows only with these.
    if (this.hasHeaderTarget) this.headerTarget.hidden = count < 2
  }

  #clearDropHighlight() {
    for (const el of this.element.querySelectorAll(".bg-primary\\/10")) {
      el.classList.remove("bg-primary/10")
    }
  }

  /**
   * A row and every group nested inside it.
   *
   * A toggle on "Shapes" has to reach the entities in the groups under it too,
   * not just the ones sitting directly in it.
   */
  #branchIdsFrom(row) {
    const ids = [row.dataset.subsetId]
    for (const nested of row.querySelectorAll("[data-subset-id]")) {
      ids.push(nested.dataset.subsetId)
    }

    // The flat tree's one node owns every row that was painted into it,
    // including the ones `parentFor` parented to a container this tree does not
    // render (see `#soleContainer`). The cascade matches on a row's OWN
    // `parentId`, so without their ids here, hiding "Entities" hid the node and
    // nothing in it — the list stayed fully visible under a closed eye.
    if (row === this.#soleContainer()) {
      for (const painted of this.entityRows ?? []) ids.push(painted.parentId)
    }

    return [...new Set(ids)]
  }

  /**
   * The subsets the tree currently shows.
   *
   * Two readers, one message, because both need the same answer to the same
   * question — which groups exist right now:
   *
   *  - The subsets mirror takes the ids. An `entity.subsetId` naming a group
   *    that has been deleted must fall through to the type rule rather than
   *    hiding the entity, and only this side knows what the tree holds after a
   *    reload.
   *  - The breakdown panel takes the NAMES. Its zone headings have to read the
   *    same as the tree's rows, and the breakdown store keeps no subsets on a
   *    Hotwire page — its only writers are React.
   */
  #publishKnownSubsets() {
    const rows = [
      ...this.element.querySelectorAll("[data-subset-kind='subset']"),
    ]
    this.#tellCanvas("subsets:known-subsets", {
      ids: rows.map((row) => row.dataset.subsetId),
      subsets: rows.map((row) => ({
        id: row.dataset.subsetId,
        name: row.dataset.subsetName ?? "",
        // Absent and empty are the same thing to a heading, and `.compact` on
        // the Ruby side drops the attribute entirely when there is no text.
        description: row.dataset.subsetDescription || null,
      })),
    })
  }

  #reloadTree() {
    // By id. The editor page has more than one frame — the command palette's is
    // earlier in the DOM — and a bare tag selector picks whichever comes first.
    const frame = this.element.querySelector("#floorplan-subsets-tree")
    if (!frame) return

    // First reload sets the src; Turbo re-fetches it on every later call.
    if (frame.src) frame.reload()
    else frame.src = this.reloadPathValue
  }

  /**
   * Tell `SaveManager` the version the subsets API just bumped.
   *
   * Without this the next autosave sends a stale base version and 409s after
   * EVERY group operation.
   */
  #adoptContentVersion(body) {
    const version = body?.content_version ?? body?.contentVersion
    if (version == null) return

    this.#tellCanvas("subsets:content-version", { version })
  }

  #tellCanvas(name, detail) {
    this.dispatch(name, { detail, prefix: false, bubbles: true })
  }

  async #safeJson(response) {
    try {
      return await response.json
    } catch {
      // A 204, or a body that is not JSON. Neither is an error here — it only
      // means there is no version to adopt.
      return null
    }
  }

  #subsetsPath() {
    return `/api/v1/drawings/${this.drawingIdValue}/subsets`
  }

  #subsetPath(id) {
    return `${this.#subsetsPath()}/${id}`
  }

  #batchPath() {
    return `${this.#subsetsPath()}/batch_update`
  }

  #openDialog(id) {
    document.getElementById(id)?.showModal()
  }

  #closeDialog(id) {
    document.getElementById(id)?.close()
  }
}
